import { Fragment, useMemo } from 'react';

// Third-party
import Box from '@mui/material/Box';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { AvatarGroup, useTheme } from '@mui/material';

// Redux-toolkit
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { MemoizedBaseAvatar, BaseTooltip, BaseCheckbox } from '@mgm/common/components/base';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { getSelectedSubmission } from '../../../../store/tasklists/selectors';

// Hooks
import { useAppSelector } from '../../../../hooks/useAppStore';

// Components
import { SubmissionBlockWrapper } from '../../Shared';

// Utils
import { getFormattedTasks } from '../../Shared/utils';

// Types
import { TSubmissionTask } from '../../../../types';

const SubmissionBlock = () => {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const selectedSubmission = useAppSelector(getSelectedSubmission);
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);

  const getTasks = useMemo(() => {
    const tasks = [
      ...(selectedSubmission?.tasks || ([] as Array<TSubmissionTask>)),
    ];

    const formattedTasks = getFormattedTasks(tasks, personnel.email);

    return formattedTasks.reduce(
      (acc: Array<TSubmissionTask>, task: TSubmissionTask) => {
        const avatars = task.doneBy?.map(
          (doneBy, index: number) => ({
            name: doneBy.name,
            src: doneBy.avatarFile,
            id: index,
          }),
        );

        acc.push({
          ...task,
          avatars,
        });

        return acc;
      },
      [],
    );
  }, [selectedSubmission.tasks]);

  return (
    <SubmissionBlockWrapper selectedSubmission={selectedSubmission}>
      <Box>
        <CardHeader
          sx={{
            px: isMobile ? 1 : 3,
          }}
          title={selectedSubmission.title}
        />
        <Box sx={{ px: isMobile ? 1.5 : 3, py: 1 }}>
          {getTasks?.map((task) => (
            <Fragment key={task.taskName}>
              <Stack direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
                <FormControlLabel
                  control={<BaseCheckbox disabled value={task} checked={task.isDone} />}
                  label={(
                    <Typography
                      variant="body2"
                      sx={{
                        ...(task.isDone && {
                          color: 'text.disabled',
                          textDecoration: 'line-through',
                        }),
                      }}
                    >
                      {task.taskName}
                    </Typography>
                  )}
                />
                <AvatarGroup>
                  {task.avatars?.map((avatar) => (
                    <BaseTooltip key={avatar.name} tooltipText={avatar.name}>
                      <MemoizedBaseAvatar
                        sx={{
                          marginRight: '0',
                        }}
                        avatarName={avatar.name}
                        avatarFile={avatar.src}
                      />
                    </BaseTooltip>
                  ))}
                </AvatarGroup>
              </Stack>
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Fragment>
          ))}
        </Box>
      </Box>
    </SubmissionBlockWrapper>
  );
};

export {
  SubmissionBlock,
};
